// Базовые элементы интерфейса: кнопка (с подсказкой и сочетанием клавиш), выпадающее меню (с клавиатурой),
// всплывающий слой, модальное окно, поля формы, переключатели, подсказки (TooltipHost).
import { useEffect, useLayoutEffect, useRef, useState, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { Icon } from './icons';
import type { MenuItemSpec } from '../core/registry';
import { ctx, runCommand } from '../core/ctx';
import { registry } from '../core/registry';

export function Btn(props: {
  icon?: string; label?: string; title?: string; onClick?: (e: React.MouseEvent) => void; active?: boolean;
  disabled?: boolean; primary?: boolean; large?: boolean; className?: string; children?: ReactNode; testid?: string;
  /** сочетание клавиш для подсказки ('Mod-s') и пояснение (вторая строка подсказки) */
  shortcut?: string; desc?: string; danger?: boolean; iconSize?: number;
}) {
  const { icon, label, title, onClick, active, disabled, primary, large, className, children, testid, shortcut, desc, danger, iconSize } = props;
  const tip = title || label || '';
  return (
    <button type="button" data-testid={testid}
      className={['btn', large ? 'btn-large' : '', active ? 'is-active' : '', primary ? 'btn-primary' : '', danger ? 'btn-danger' : '', className || ''].join(' ')}
      data-tip={tip || undefined} data-tip-desc={desc || undefined} data-tip-key={shortcut ? prettyShortcut(shortcut) : undefined}
      aria-label={tip || undefined} aria-pressed={active === undefined ? undefined : active}
      disabled={disabled} onMouseDown={(e) => e.preventDefault()} onClick={onClick}>
      {icon && <Icon name={icon} size={iconSize || (large ? 26 : 16)} />}
      {label && <span className="btn-label">{label}</span>}
      {children}
    </button>
  );
}

/** Подсказка с сочетанием клавиш: 'Mod-Shift-b' -> 'Ctrl+Shift+B'. */
export function prettyShortcut(s?: string) {
  if (!s) return '';
  const parts = s.split(/-(?!$)/);
  const map: Record<string, string> = { Mod: 'Ctrl', Ctrl: 'Ctrl', Alt: 'Alt', Shift: 'Shift', Meta: 'Win', Enter: 'Enter',
    ArrowUp: '↑', ArrowDown: '↓', ArrowLeft: '←', ArrowRight: '→', Escape: 'Esc', Backspace: 'Backspace', Space: 'Пробел', ' ': 'Пробел' };
  return parts.map((p) => map[p] || (p.length === 1 ? p.toUpperCase() : p)).join('+');
}

export function Kbd({ keys }: { keys: string }) {
  return <span className="kbd-group">{prettyShortcut(keys).split('+').map((k, i) => <kbd key={i}>{k}</kbd>)}</span>;
}

// ------------------------------------------------------------------------------------------------ меню
export function MenuList({ items, onDone, autoFocus }: { items: MenuItemSpec[]; onDone: () => void; autoFocus?: boolean }) {
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (autoFocus) (ref.current?.querySelector('.menu-item:not(:disabled)') as HTMLElement | null)?.focus();
  }, [autoFocus]);
  const onKey = (e: React.KeyboardEvent) => {
    const btns = Array.from(ref.current?.querySelectorAll(':scope > .menu-item:not(:disabled), :scope > .menu-sub > .menu-item') || []) as HTMLElement[];
    const i = btns.indexOf(document.activeElement as HTMLElement);
    if (e.key === 'ArrowDown') { e.preventDefault(); btns[(i + 1) % btns.length]?.focus(); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); btns[(i - 1 + btns.length) % btns.length]?.focus(); }
    else if (e.key === 'Home') { e.preventDefault(); btns[0]?.focus(); }
    else if (e.key === 'End') { e.preventDefault(); btns[btns.length - 1]?.focus(); }
  };
  return (
    <div className="menu" role="menu" ref={ref} onKeyDown={onKey}>
      {items.map((it, i) => {
        if (it.separator) return <div key={i} className="menu-sep" role="separator" />;
        const cmd = it.command ? registry.commands.get(it.command) : undefined;
        const disabled = it.disabled || (cmd?.isEnabled ? !cmd.isEnabled(ctx) : false);
        const checked = it.checked ?? (cmd?.isActive ? cmd.isActive(ctx) : undefined);
        if (it.children?.length) {
          return <SubMenu key={i} item={it} onDone={onDone} />;
        }
        return (
          <button key={i} type="button" role={checked === undefined ? 'menuitem' : 'menuitemcheckbox'} aria-checked={checked}
            className={'menu-item' + (checked ? ' is-checked' : '')} disabled={disabled}
            data-testid={it.id ? 'menu-' + it.id : undefined} title={it.hint}
            onMouseDown={(e) => e.preventDefault()}
            onClick={() => { onDone(); if (it.onClick) it.onClick(ctx); else if (it.command) runCommand(it.command, it.arg); }}>
            <span className="menu-ico">{checked ? <Icon name="ok" size={14} /> : <Icon name={it.icon || cmd?.icon} size={15} />}</span>
            <span className="menu-label">{it.label || cmd?.title}</span>
            <span className="menu-key">{prettyShortcut(it.shortcut || cmd?.shortcut)}</span>
          </button>
        );
      })}
    </div>
  );
}

function SubMenu({ item, onDone }: { item: MenuItemSpec; onDone: () => void }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="menu-sub" onMouseEnter={() => setOpen(true)} onMouseLeave={() => setOpen(false)}
      onKeyDown={(e) => { if (e.key === 'ArrowRight') { e.preventDefault(); setOpen(true); } else if (e.key === 'ArrowLeft' && open) { e.preventDefault(); e.stopPropagation(); setOpen(false); } }}>
      <button type="button" className="menu-item" role="menuitem" aria-haspopup="menu" aria-expanded={open}
        onMouseDown={(e) => e.preventDefault()} onClick={() => setOpen(!open)}>
        <span className="menu-ico"><Icon name={item.icon} size={15} /></span>
        <span className="menu-label">{item.label}</span>
        <span className="menu-key"><Icon name="chevron-right" size={13} /></span>
      </button>
      {open && <div className="menu-sub-pop"><MenuList items={item.children!} onDone={onDone} /></div>}
    </div>
  );
}

/** Всплывающий слой, привязанный к элементу-якорю или к точке. Закрывается кликом снаружи и Esc. */
export function Popover({ anchor, point, onClose, children, align = 'left', className }: {
  anchor?: HTMLElement | null; point?: { x: number; y: number }; onClose: () => void; children: ReactNode;
  align?: 'left' | 'right'; className?: string;
}) {
  const ref = useRef<HTMLDivElement>(null);
  const [pos, setPos] = useState<{ left: number; top: number }>({ left: -9999, top: -9999 });
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    const w = el.offsetWidth; const h = el.offsetHeight;
    let left = 0; let top = 0;
    if (anchor) {
      const r = anchor.getBoundingClientRect();
      left = align === 'right' ? r.right - w : r.left; top = r.bottom + 2;
    } else if (point) { left = point.x; top = point.y; }
    left = Math.max(4, Math.min(left, window.innerWidth - w - 4));
    if (top + h > window.innerHeight - 4) top = Math.max(4, (anchor ? anchor.getBoundingClientRect().top - h - 2 : window.innerHeight - h - 4));
    setPos({ left, top });
  }, [anchor, point, align]);
  useEffect(() => {
    const down = (e: MouseEvent) => { if (ref.current && !ref.current.contains(e.target as Node) && !(anchor && anchor.contains(e.target as Node))) onClose(); };
    const key = (e: KeyboardEvent) => { if (e.key === 'Escape') { e.stopPropagation(); onClose(); anchor?.focus?.(); } };
    document.addEventListener('mousedown', down, true);
    document.addEventListener('keydown', key, true);
    return () => { document.removeEventListener('mousedown', down, true); document.removeEventListener('keydown', key, true); };
  }, [onClose, anchor]);
  return createPortal(<div ref={ref} className={'popover ' + (className || '')} style={{ left: pos.left, top: pos.top }}>{children}</div>, document.body);
}

// ------------------------------------------------------------------------------------------------ окно
export function Modal({ title, onClose, children, footer, width = 560, testid, className }: {
  title: string; onClose: () => void; children: ReactNode; footer?: ReactNode; width?: number; testid?: string; className?: string;
}) {
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const key = (e: KeyboardEvent) => { if (e.key === 'Escape') { e.stopPropagation(); onClose(); } };
    document.addEventListener('keydown', key);
    return () => document.removeEventListener('keydown', key);
  }, [onClose]);
  useEffect(() => {
    // фокус - в первое поле окна (или на само окно), чтобы работали Tab и Esc
    const el = ref.current;
    if (!el || el.contains(document.activeElement)) return;
    const f = el.querySelector('input:not([type=hidden]):not([disabled]), textarea, select, [data-autofocus]') as HTMLElement | null;
    (f || el).focus();
  }, []);
  return createPortal(
    <div className="modal-back" onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div ref={ref} tabIndex={-1} className={'modal ' + (className || '')} style={{ width }} role="dialog" aria-modal="true" aria-label={title} data-testid={testid}>
        <div className="modal-head">
          <span>{title}</span>
          <button type="button" className="modal-x" onClick={onClose} aria-label="Закрыть" data-tip="Закрыть" data-tip-key="Esc"><Icon name="close" /></button>
        </div>
        <div className="modal-body">{children}</div>
        {footer && <div className="modal-foot">{footer}</div>}
      </div>
    </div>, document.body);
}

export function Field({ label, children, hint, wide }: { label: string; children: ReactNode; hint?: string; wide?: boolean }) {
  return (
    <label className={'field' + (wide ? ' field-wide' : '')}>
      <span className="field-label">{label}</span>
      {children}
      {hint && <span className="field-hint">{hint}</span>}
    </label>
  );
}

/** Переключатель (вкл/выкл) с подписью. */
export function Toggle({ checked, onChange, label, testid }: { checked: boolean; onChange: (v: boolean) => void; label: string; testid?: string }) {
  return (
    <label className="toggle" data-testid={testid}>
      <input type="checkbox" role="switch" checked={checked} onChange={(e) => onChange(e.target.checked)} />
      <span className="toggle-track"><span className="toggle-thumb" /></span>
      <span>{label}</span>
    </label>
  );
}

/** Сегментированный выбор (как вкладки-кнопки). */
export function Segmented<T extends string>({ value, options, onChange, testid }: {
  value: T; options: { id: T; label: string; icon?: string }[]; onChange: (v: T) => void; testid?: string;
}) {
  return (
    <div className="segmented" role="radiogroup" data-testid={testid}>
      {options.map((o) => (
        <button key={o.id} type="button" role="radio" aria-checked={value === o.id} className={value === o.id ? 'is-active' : ''}
          onMouseDown={(e) => e.preventDefault()} onClick={() => onChange(o.id)} data-testid={testid ? `${testid}-${o.id}` : undefined}>
          {o.icon && <Icon name={o.icon} size={14} />}<span>{o.label}</span>
        </button>
      ))}
    </div>
  );
}

/** Пустое состояние: значок, заголовок, пояснение, действия. */
export function Empty({ icon, title, text, children }: { icon?: string; title: string; text?: ReactNode; children?: ReactNode }) {
  return (
    <div className="empty">
      {icon && <div className="empty-ico"><Icon name={icon} size={30} /></div>}
      <div className="empty-title">{title}</div>
      {text && <div className="empty-text">{text}</div>}
      {children && <div className="empty-actions">{children}</div>}
    </div>
  );
}

// ------------------------------------------------------------------------------------------------ подсказки
/** Подсказки по наведению для любых элементов с data-tip (+ data-tip-desc, data-tip-key). Одна на приложение. */
export function TooltipHost() {
  const [tip, setTip] = useState<{ x: number; y: number; below: boolean; title: string; desc?: string; key?: string } | null>(null);
  useEffect(() => {
    let timer = 0;
    let cur: HTMLElement | null = null;
    const hide = () => { window.clearTimeout(timer); cur = null; setTip(null); };
    const over = (e: PointerEvent) => {
      const el = (e.target as HTMLElement)?.closest?.('[data-tip]') as HTMLElement | null;
      if (el === cur) return;
      hide();
      if (!el || e.pointerType === 'touch') return;
      cur = el;
      timer = window.setTimeout(() => {
        if (!cur || !document.contains(cur)) return;
        const r = cur.getBoundingClientRect();
        const below = r.bottom + 70 < window.innerHeight;
        setTip({ x: r.left + r.width / 2, y: below ? r.bottom + 6 : r.top - 6, below,
          title: cur.dataset.tip || '', desc: cur.dataset.tipDesc, key: cur.dataset.tipKey });
      }, 450);
    };
    document.addEventListener('pointerover', over);
    document.addEventListener('pointerdown', hide, true);
    document.addEventListener('keydown', hide, true);
    window.addEventListener('blur', hide);
    return () => {
      document.removeEventListener('pointerover', over);
      document.removeEventListener('pointerdown', hide, true);
      document.removeEventListener('keydown', hide, true);
      window.removeEventListener('blur', hide);
    };
  }, []);
  const ref = useRef<HTMLDivElement>(null);
  const [dx, setDx] = useState(0);
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el || !tip) return;
    const w = el.offsetWidth;
    const left = tip.x - w / 2;
    setDx(left < 6 ? 6 - left : left + w > window.innerWidth - 6 ? window.innerWidth - 6 - (left + w) : 0);
  }, [tip]);
  if (!tip || (!tip.title && !tip.desc)) return null;
  return createPortal(
    <div ref={ref} className={'tooltip' + (tip.below ? '' : ' is-above')} role="tooltip"
      style={{ left: tip.x + dx, top: tip.y }}>
      <div className="tooltip-title">{tip.title}{tip.key && <span className="tooltip-key">{tip.key}</span>}</div>
      {tip.desc && tip.desc !== tip.title && <div className="tooltip-desc">{tip.desc}</div>}
    </div>, document.body);
}
